import React from 'react';
import { create } from 'zustand';
import { DialogProps } from '@mui/material';
// Define the state for the Dialog
export interface DialogState {
  isDialogOpen: boolean;
  dialogTitle?: string;
  dialogContent: React.ReactNode;
  maxWidth?: DialogProps['maxWidth'];
  openDialog: (options: DialogOptions) => void;
  closeDialog: () => void;
  requiredInteraction: boolean;
  previousDialogContent: React.ReactNode[];
  isDialogRendered: boolean;
  setDialogRendered: (rendered: boolean) => void;
}
// Define options for the Dialog

type DialogOptions = Pick<DialogState, 'dialogContent'> &
  Partial<Pick<DialogState, 'maxWidth' | 'requiredInteraction'>>;

// Create a store for managing the dialog state
/**
 * Represents the state and actions for managing a dialog.
 */
export const useDialogStore = create<DialogState>((set, get) => ({
  /**
   * Indicates whether the dialog is currently open or not.
   */
  isDialogOpen: false,
  /**
   * The content of the dialog.
   */
  dialogContent: null,
  /**
   * The previous content of the dialog.
   * Used in the open and close methods when a dialog opens another dialog.
   */
  previousDialogContent: [],
  /**
   * Indicates whether the dialog requires user interaction or not.
   */
  requiredInteraction: false,
  /**
   * Set the max width of the modal
   */
  maxWidth: 'sm',
  /**
   * Opens the dialog with the specified options.
   * @param options - The options for the dialog.
   */
  openDialog: (options: DialogOptions) => {
    const previousContent = get().dialogContent;
    set((state) => {
      if (state.isDialogOpen) {
        state.previousDialogContent.push(previousContent);
      }
      return {
        ...options,
        isDialogOpen: true,
        maxWidth: options.maxWidth ?? 'sm',
      };
    });
  },
  /**
   * Closes the dialog and restores the previous content.
   */
  closeDialog: () =>
    set((state) => {
      const previousContent = state.previousDialogContent.pop();
      const currentContent = get().dialogContent;
      return {
        isDialogOpen: !!previousContent,
        dialogContent: previousContent || currentContent,
        previousDialogContent: state.previousDialogContent,
      };
    }),
  isDialogRendered: false,
  setDialogRendered: (rendered) => set({ isDialogRendered: rendered }),
}));
